import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { setClientSettings } from "./clientSettingsSlice";

// Define a type for the slice state
export interface AuthState {
  isAuthenticated: boolean;
  isFirstLogin: boolean;
  token: string | null;
  clientId: string | null;
  userId: string | null;
  userName: string | null;
  firstName: string | null;
  lastName: string | null;
  exp: number | null;
  permissions: number[];
  secRoleId: number | null;
  cultureId: string | null;
  ipAddress: string | null;
  isSubscriptionLocked?: boolean;
  overdueAmount?: number;
  overduePeriodDays?: number;
  currency?: string;
  currencySymbol?: string;
}

// Define the initial state using that type
const initialState: AuthState = {
  isAuthenticated: false,
  isFirstLogin: false,
  clientId: null,
  userId: null,
  userName: null,
  firstName: null,
  lastName: null,
  cultureId: null,
  permissions: [],
  secRoleId: null,
  exp: null,
  token: null,
  ipAddress: null,
  isSubscriptionLocked: false,
  overdueAmount: 0,
  overduePeriodDays: 0,
  currency: "USD",
  currencySymbol: "$",
};

export const authSlice = createSlice({
  name: "auth",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    login(
      state,
      action: PayloadAction<{
        clientId: string | null;
        userId: string | null;
        userName: string | null;
        firstName: string | null;
        lastName: string | null;
        exp: number | null;
        permissions: number[] | [];
        secRoleId: number | null;
        cultureId: string | null;
        token: string | null;
        ipAddress: string | null;
        isSubscriptionLocked?: boolean;
        overdueAmount?: number;
        overduePeriodDays?: number;
        currency?: string;
        currencySymbol?: string;
      }>,
    ) {
      state.isAuthenticated = true;
      state.userName = action.payload.userName;
      state.firstName = action.payload.firstName;
      state.lastName = action.payload.lastName;
      state.clientId = action.payload.clientId;
      state.userId = action.payload.userId;
      state.cultureId = action.payload.cultureId;
      state.permissions = action.payload.permissions;
      state.secRoleId = action.payload.secRoleId;
      state.exp = action.payload.exp;
      state.token = action.payload.token;
      state.ipAddress = action.payload.ipAddress;
      state.isSubscriptionLocked = action.payload.isSubscriptionLocked;
      state.overdueAmount = action.payload.overdueAmount;
      state.overduePeriodDays = action.payload.overduePeriodDays;
      state.currency = action.payload.currency;
      state.currencySymbol = action.payload.currencySymbol;
    },
    logout(state) {
      state.isAuthenticated = false;
      state.userName = null;
      state.firstName = null;
      state.lastName = null;
      state.token = null;
      state.clientId = null;
      state.userId = null;
      state.cultureId = null;
      state.permissions = [];
      state.secRoleId = null;
      state.exp = null;
      state.ipAddress = null;
      state.isSubscriptionLocked = false;
      state.overdueAmount = 0;
      state.overduePeriodDays = 0;
      state.currency = "USD";
      state.currencySymbol = "$";
    },
    setPermissions(
      state,
      action: PayloadAction<{
        permissions: number[];
      }>,
    ) {
      state.permissions = action.payload.permissions;
    },
  },
});

export const { login, logout, setPermissions } = authSlice.actions;

// Other code such as selectors can use the imported `RootState` type

export const getAuthState = (state: RootState) => state.auth;
export const getAuthStatus = (state: RootState) => state.auth.isAuthenticated;

export default authSlice.reducer;
